import type { Unit } from '@/utils/units';
import api from './api';

export interface StockMovementCreate {
  product_id: number;
  movement_type: 'purchase' | 'sale' | 'adjustment' | 'loss' | 'expired' | 'return' | 'internal_use' | 'transfer';
  quantity: number;
  // OBLIGATORIO en la practica: sin esto el backend asume el display_unit del producto.
  // Como aca siempre mandamos `quantity` ya convertida a la unidad BASE (gramos para
  // peso, unidades para count), omitirlo hace que un granel en kg se multiplique x1000.
  quantity_unit?: Unit;
  unit_cost?: number;
  batch_number?: string;
  expiration_date?: string;
  notes?: string;
}

export interface StockMovement {
  id: number;
  tenant_id: number;
  product_id: number;
  user_id?: number;
  movement_type: string;
  quantity: number;
  previous_stock: number;
  new_stock: number;
  unit_cost?: number;
  reference_id?: number;
  reference_type?: string;
  batch_number?: string;
  expiration_date?: string;
  notes?: string;
  created_at: string;
  product_name?: string;
  user_name?: string;
}

export interface InventorySummary {
  total_products: number;
  low_stock_count: number;
  expired_count: number;
  expiring_soon_count: number;
  total_stock_value: number;
}

export interface ProductStockAlert {
  id: number;
  name: string;
  code?: string;
  current_stock: number;
  minimum_stock: number;
  expiration_date?: string;
  alert_type: 'low_stock' | 'expired' | 'expiring_soon';
  days_until_expiration?: number;
}

export interface MovementFilters {
  product_id?: number;
  movement_type?: string;
  start_date?: string;
  end_date?: string;
  skip?: number;
  limit?: number;
}

export const inventoryService = {
  createMovement: async (data: StockMovementCreate): Promise<StockMovement> => {
    const response = await api.post('/inventory/movements', data);
    return response.data;
  },

  getMovements: async (filters?: MovementFilters): Promise<StockMovement[]> => {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.movement_type) params.append('movement_type', filters.movement_type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.skip) params.append('skip', String(filters.skip));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/inventory/movements?${params.toString()}`);
    return response.data;
  },

  getSummary: async (expiringDays: number = 60): Promise<InventorySummary> => {
    const response = await api.get(`/inventory/summary?expiring_days=${expiringDays}`);
    return response.data;
  },

  getAlerts: async (expiringDays: number = 60): Promise<ProductStockAlert[]> => {
    const response = await api.get(`/inventory/alerts?expiring_days=${expiringDays}`);
    return response.data;
  },
};
